<template>
  <div class="home-page">
    <FloatingNavbar />

    <!-- Hero -->
    <section class="home-hero">
      <VParallax
        :src="parallaxImage"
        height="88vh"
        class="home-hero__parallax"
        gradient="to top, rgba(15, 23, 42, 0.88), rgba(30, 41, 78, 0.52)"
      >
        <div
          class="home-hero__content fill-height d-flex flex-column justify-center"
        >
          <VContainer class="home-hero__container">
            <VRow justify="center">
              <VCol cols="12" md="10" lg="8" class="text-center">
                <p class="home-hero__kicker text-white mb-5">
                  Federal Republic of Somalia · MoLSA TVET
                </p>

                <h1 class="home-hero__title text-white mb-5">
                  Empowering Somalia’s<br class="d-none d-sm-block" />
                  future professionals
                </h1>

                <p class="home-hero__lead text-white mx-auto mb-10">
                  National platform for technical & vocational education
                  institutions, trainers & learners in one place.
                </p>

                <div
                  class="home-hero__actions d-flex flex-wrap justify-center ga-3"
                >
                  <VBtn
                    color="indigo"
                    variant="flat"
                    size="large"
                    rounded="lg"
                    class="home-hero__btn text-white"
                    to="/register-institution"
                  >
                    <VIcon start>mdi-domain</VIcon>
                    Register institution
                    <VIcon end>mdi-chevron-right</VIcon>
                  </VBtn>
                  <VBtn
                    variant="flat"
                    color="indigo"
                    size="large"
                    rounded="lg"
                    class="home-hero__btn text-white"
                    to="/register-trainer"
                  >
                    <VIcon start>mdi-human-male-board-poll</VIcon>
                    Register trainer
                    <VIcon end>mdi-chevron-right</VIcon>
                  </VBtn>
                  <VBtn
                    variant="outlined"
                    color="white"
                    size="large"
                    rounded="lg"
                    class="home-hero__btn"
                    to="/online-applications"
                  >
                    <VIcon start>mdi-file-document-edit-outline</VIcon>
                    Track application
                    <VIcon end>mdi-chevron-right</VIcon>
                  </VBtn>
                </div>
              </VCol>
            </VRow>
          </VContainer>
        </div>
      </VParallax>
    </section>

    <!-- Stats -->
    <VContainer class="home-stats-wrap">
      <VRow class="home-stats-row" dense>
        <VCol
          v-for="stat in displayStats"
          :key="stat.key"
          cols="12"
          sm="6"
          md="3"
        >
          <VCard
            class="home-stat-card h-100"
            rounded="xl"
            variant="flat"
            elevation="0"
          >
            <VCardText class="home-stat-card__inner text-center">
              <VAvatar
                :color="stat.color"
                variant="tonal"
                size="48"
                rounded="lg"
                class="mb-4"
              >
                <VIcon :icon="stat.icon" size="26" />
              </VAvatar>
              <div class="home-stat-value mb-2">
                <template v-if="statsLoading">
                  <VProgressCircular indeterminate size="22" width="2" />
                </template>
                <template v-else>{{ stat.value }}</template>
              </div>
              <div class="home-stat-label">{{ stat.label }}</div>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>
    </VContainer>

    <!-- Quick access -->
    <VContainer class="home-section home-section--links">
      <div class="home-section__intro text-center">
        <p class="home-section-kicker mb-3">What you can do</p>
        <h2 class="home-section-title mb-4">
          Explore the national TVET ecosystem
        </h2>
        <p class="home-section-desc mx-auto">
          Register institutions, discover certified trainers, submit
          applications and access resources built for MoLSA and TVET
          stakeholders across Somalia.
        </p>
      </div>

      <VRow class="ga-0">
        <VCol
          v-for="card in quickLinks"
          :key="card.to"
          cols="12"
          sm="6"
          lg="4"
          class="pa-3"
        >
          <VCard
            :to="card.to"
            class="home-link-card h-100"
            rounded="xl"
            variant="outlined"
            hover
          >
            <VCardText class="home-link-card__inner">
              <VAvatar
                :color="card.color"
                variant="tonal"
                size="48"
                rounded="lg"
                class="mb-5"
              >
                <VIcon :icon="card.icon" size="24" />
              </VAvatar>
              <h3 class="home-link-title mb-3">{{ card.title }}</h3>
              <p class="home-link-desc mb-5">{{ card.description }}</p>
              <span class="home-link-action">
                Open
                <VIcon end size="18">mdi-arrow-right</VIcon>
              </span>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>
    </VContainer>

    <!-- Services -->
    <section class="home-section home-section--muted">
      <VContainer class="home-section__inner">
        <div class="home-section__intro text-center">
          <p class="home-section-kicker mb-3">
            Accreditation &amp; registration
          </p>
          <h2 class="home-section-title mb-4">Our services</h2>
          <p class="home-section-desc mx-auto">
            End-to-end support for TVET institutions and workforce development
            in Somalia.
          </p>
        </div>
        <InstitutionApplications />
      </VContainer>
    </section>

    <PartnerSlides />

    <FooterPublic />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import axiosApi from "@/utils/api";
import FloatingNavbar from "@/components/FloatingNavbar.vue";
import FooterPublic from "@/components/FooterPublic.vue";
import PartnerSlides from "@/components/PartnerSlides.vue";
import InstitutionApplications from "@/components/InstitutionsApplications.vue";
import parallaxImage from "@/assets/images/paralax/somali-tvet-graduation.jpg";

const statsLoading = ref(true);
const statsData = ref({
  total_institutions: 0,
  total_trainers: 0,
  total_students: 0,
  partners: 3,
});

const formatCount = (n) => {
  if (n == null) return "0";
  return Number(n).toLocaleString();
};

const displayStats = computed(() => [
  {
    key: "institutions",
    icon: "mdi-domain",
    color: "indigo",
    value: formatCount(statsData.value.total_institutions),
    label: "Registered institutions",
  },
  {
    key: "trainers",
    icon: "mdi-human-male-board-poll",
    color: "indigo",
    value: formatCount(statsData.value.total_trainers),
    label: "Certified trainers",
  },
  {
    key: "graduates",
    icon: "mdi-school-outline",
    color: "indigo",
    value: formatCount(statsData.value.total_students),
    label: "Learners & graduates",
  },
  {
    key: "partners",
    icon: "mdi-handshake-outline",
    color: "indigo",
    value: String(statsData.value.partners),
    label: "Development partners",
  },
]);

const quickLinks = [
  {
    title: "Institutions",
    description: "Search registered and licensed TVET providers nationwide.",
    icon: "mdi-domain",
    color: "indigo",
    to: "/tvet-institutions",
  },
  {
    title: "Trainers",
    description: "Browse certified trainers, assessors and verifiers.",
    icon: "mdi-human-male-board-poll",
    color: "indigo",
    to: "/tvet-trainers",
  },
  {
    title: "Applications",
    description: "Submit institution or trainer registration online.",
    icon: "mdi-laptop",
    color: "indigo",
    to: "/online-applications",
  },
];

const loadStats = async () => {
  statsLoading.value = true;
  try {
    const { data: body } = await axiosApi.get("/api/v2/public/statistics");
    const payload = body?.data ?? body;
    if (payload) {
      statsData.value = {
        total_institutions: payload.total_institutions ?? 0,
        total_trainers: payload.total_trainers ?? 0,
        total_students: payload.total_students ?? 0,
        partners: 3,
      };
    }
  } catch {
    try {
      const [instRes, trainerRes] = await Promise.all([
        axiosApi.get("/api/v2/public/institutions/directory"),
        axiosApi.get("/api/v2/public/trainers/directory"),
      ]);
      statsData.value.total_institutions =
        instRes.data?.stats?.total ?? instRes.data?.data?.length ?? 0;
      statsData.value.total_trainers =
        trainerRes.data?.stats?.total ?? trainerRes.data?.data?.length ?? 0;
    } catch {
      // Keep defaults
    }
  } finally {
    statsLoading.value = false;
  }
};

onMounted(() => {
  loadStats();
});
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Public+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&family=Sora:wght@500;600;700;800&display=swap");

.home-page {
  --home-font-display: "Sora", "Public Sans", sans-serif;
  --home-font-body: "Public Sans", sans-serif;
  --home-space-xs: 0.5rem;
  --home-space-sm: 0.75rem;
  --home-space-md: 1.25rem;
  --home-space-lg: 2rem;
  --home-space-xl: 3.5rem;
  --home-space-2xl: 5.5rem;
  --home-max: 1180px;

  font-family: var(--home-font-body);
  font-size: 1rem;
  line-height: 1.6;
  letter-spacing: 0.01em;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  background:
    radial-gradient(
      ellipse 80% 50% at 50% -10%,
      rgba(63, 81, 181, 0.06),
      transparent 60%
    ),
    rgb(var(--v-theme-background));
  color: rgb(var(--v-theme-on-surface));
}

.home-hero__parallax :deep(.v-parallax__content) {
  display: flex;
  flex-direction: column;
}

.home-hero__container {
  max-width: var(--home-max);
  padding-block: clamp(4.5rem, 12vh, 7.5rem);
}

.home-hero__kicker {
  display: inline-block;
  font-family: var(--home-font-body);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  opacity: 0.92;
  margin: 0;
  animation: home-fade-up 0.7s ease both;
}

.home-hero__title {
  font-family: var(--home-font-display);
  font-size: clamp(2.35rem, 5.8vw, 3.75rem);
  font-weight: 700;
  letter-spacing: -0.035em;
  line-height: 1.08;
  text-wrap: balance;
  text-shadow: 0 2px 28px rgba(0, 0, 0, 0.32);
  animation: home-fade-up 0.75s ease 0.06s both;
}

.home-hero__lead {
  font-family: var(--home-font-body);
  font-size: clamp(1.05rem, 2.1vw, 1.25rem);
  line-height: 1.7;
  max-width: 34rem;
  opacity: 0.9;
  font-weight: 400;
  letter-spacing: 0.01em;
  animation: home-fade-up 0.75s ease 0.12s both;
}

.home-hero__actions {
  animation: home-fade-up 0.8s ease 0.18s both;
}

.home-hero__btn {
  font-family: var(--home-font-body);
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.01em;
  padding-inline: 1.35rem !important;
  min-height: 3rem;
}

.home-stats-wrap {
  position: relative;
  z-index: 2;
  max-width: var(--home-max);
  margin-top: clamp(-5.5rem, -9vw, -4rem);
  margin-bottom: 0;
  padding-bottom: 0;
}

.home-stats-row {
  margin-top: 0;
}

.home-stat-card {
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  box-shadow: 0 14px 40px rgba(15, 23, 42, 0.07);
  transition:
    transform 0.28s ease,
    box-shadow 0.28s ease;
}

.home-stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.11);
}

.home-stat-card__inner {
  padding: 1.5rem 1.25rem !important;
}

.home-stat-value {
  font-family: var(--home-font-display);
  font-size: clamp(1.75rem, 3vw, 2.15rem);
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.1;
  color: rgb(var(--v-theme-on-surface));
  min-height: 2.4rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.home-stat-label {
  font-family: var(--home-font-body);
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.35;
  color: rgba(var(--v-theme-on-surface), 0.6);
  letter-spacing: 0.02em;
}

.home-section {
  max-width: var(--home-max);
}

.home-section--links {
  padding-block: var(--home-space-2xl);
}

.home-section--muted {
  max-width: none;
  padding-block: var(--home-space-2xl);
  background: rgba(var(--v-theme-on-surface), 0.03);
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.06);
}

.home-section__inner {
  max-width: var(--home-max);
}

.home-section__intro {
  margin-bottom: clamp(2.25rem, 5vw, 3.25rem);
}

.home-section-kicker {
  font-family: var(--home-font-body);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgb(var(--v-theme-primary));
  margin: 0;
}

.home-section-title {
  font-family: var(--home-font-display);
  font-size: clamp(1.65rem, 3.2vw, 2.35rem);
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.15;
  text-wrap: balance;
  color: rgb(var(--v-theme-on-surface));
  margin: 0;
}

.home-section-desc {
  font-family: var(--home-font-body);
  font-size: 1.05rem;
  line-height: 1.7;
  color: rgba(var(--v-theme-on-surface), 0.62);
  max-width: 38rem;
  margin: 0;
}

.home-link-card {
  border-color: rgba(var(--v-theme-on-surface), 0.1) !important;
  transition:
    transform 0.28s ease,
    box-shadow 0.28s ease,
    border-color 0.28s ease;
  text-decoration: none;
}

.home-link-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 18px 44px rgba(15, 23, 42, 0.1);
  border-color: rgba(var(--v-theme-primary), 0.28) !important;
}

.home-link-card__inner {
  padding: 1.75rem 1.5rem !important;
}

.home-link-title {
  font-family: var(--home-font-display);
  font-size: 1.2rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.25;
  color: rgb(var(--v-theme-on-surface));
  margin: 0;
}

.home-link-desc {
  font-family: var(--home-font-body);
  font-size: 0.95rem;
  line-height: 1.6;
  color: rgba(var(--v-theme-on-surface), 0.6);
  margin: 0;
}

.home-link-action {
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
  font-family: var(--home-font-body);
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: rgb(var(--v-theme-primary));
}

@keyframes home-fade-up {
  from {
    opacity: 0;
    transform: translateY(14px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 600px) {
  .home-hero__actions {
    flex-direction: column;
    align-items: stretch;
  }

  .home-hero__btn {
    width: 100%;
    justify-content: center;
  }

  .home-section--links,
  .home-section--muted {
    padding-block: var(--home-space-xl);
  }

  .home-stat-card__inner {
    padding: 1.25rem 1rem !important;
  }
}

@media (prefers-reduced-motion: reduce) {
  .home-hero__kicker,
  .home-hero__title,
  .home-hero__lead,
  .home-hero__actions {
    animation: none;
  }

  .home-stat-card,
  .home-link-card {
    transition: none;
  }
}
</style>
